import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, message, Popover, Tooltip } from 'antd';
import { AddDimensionBtn, AddDimensionWrapper, DimensionTitle, SelectWrapper, StyledSelect } from '../styled';
import { AddDimensionPopupType } from '../alertConfig.types';
import {
  getFilteredOptions,
  getDimensionState,
  updateDimensionState,
  isFirstDimension,
  getDimensionNameArr,
  getInsertIndex,
  getNewDimensionCombination
} from '../alertConfig.utils';
import { useUpdateConfigList } from '@components/metrics-panel-hz/hooks/alerts/useUpdateConfigList';
import useCreateDisable from '@components/aiAlerts/hooks/useCreateDisable';

export default function AddDimensionPopup({
  level,
  dimensionText,
  dimensionList,
  dimensionData,
  aiConfigList
}: AddDimensionPopupType) {
  const [selectValArr, setSelectArrState] = useState(getDimensionState(level));
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const disableCreate = useCreateDisable(selectValArr, dimensionData);
  const mesgText = isFirstDimension(level) ? `dimension` : 'dimension combination';
  const popupTitle = `Create ${dimensionText} Combination`;

  const onDimensionAddSuccess = () => {
    message.open({
      content: `${mesgText} added successfully`,
      type: 'success',
      style: { textTransform: 'capitalize' }
    });
  };

  const onDimensionAddError = () => {
    message.error(`Unable to add ${mesgText}. Please try again.`);
  };

  const { mutate: mutateAiAlertsConfig } = useUpdateConfigList(onDimensionAddSuccess, onDimensionAddError);

  const onBlur = () => {
    setSelectArrState(getDimensionState(level));
  };

  const handlePopoverChange = (isOpen: boolean): void => {
    setPopoverOpen(isOpen);
    onBlur();
  };

  const onSelectChange = (value: string, ind: number, valObj): void => {
    setSelectArrState(state => updateDimensionState(state, ind, valObj));
  };


  const onCustomDimensionCreate = () => {
    const newDimensionCombination = getNewDimensionCombination(selectValArr, level);
    const aiAlertsConfig = aiConfigList.concat();
    const insertIndex = getInsertIndex(aiAlertsConfig, level);
    aiAlertsConfig[level].dimensionData.splice(insertIndex, 0, newDimensionCombination)
    
    mutateAiAlertsConfig({ aiAlertsConfig });
    setPopoverOpen(false);
    onBlur();
  };

  const getDimensionList = (rawList) => {
    const res =  rawList.filter(dim => !getDimensionNameArr(selectValArr).includes(dim.value));
    return res.sort((a,b) => a.value?.toLowerCase().localeCompare(b.value?.toLowerCase()));
  };

  const popupContent = (
    <>
      <DimensionTitle isPopup>{popupTitle}</DimensionTitle>
      <SelectWrapper>
        {getDimensionState(level).map((dim, ind) => (
          <StyledSelect
            key={ind}
            showSearch
            value={selectValArr[ind]}
            optionFilterProp="children"
            placeholder={`Select Dimension ${ind + 1}`}
            onChange={(val, valObj) => onSelectChange(val, ind, valObj)}
            options={getDimensionList(dimensionList)}
            filterOption={(input, option) => getFilteredOptions(input, option)}
            getPopupContainer={trigger => trigger.parentElement}
            allowClear
          />
        ))}
      </SelectWrapper>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        disabled={disableCreate}
        onClick={onCustomDimensionCreate}
      >
        Add Dimension
      </Button>
    </>
  );

  return (
    <AddDimensionWrapper>
      <Popover
        placement="right"
        trigger="click"
        content={popupContent}
        open={isPopoverOpen}
        onOpenChange={handlePopoverChange}
        getPopupContainer={trigger => trigger.parentElement}
        overlayStyle={{ width: 'max-content' }}
      >
        <Tooltip title={popupTitle}>
            <AddDimensionBtn>+</AddDimensionBtn>
        </Tooltip>
      </Popover>
    </AddDimensionWrapper>
  )
}
